import axios from 'axios';
import { getDefaultModels } from './models';
import { INITIAL_JWT } from './constants';

// API 기본 설정
const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL || 'http://localhost:8081/api',
  timeout: 5000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// 요청 인터셉터
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);


// Mock data
const mockChatHistory = new Map();

// 초기 데이터 로드
const loadSavedHistory = () => {
  const savedHistory = localStorage.getItem('chatHistory');
  if (savedHistory) {
    mockChatHistory.clear();
    JSON.parse(savedHistory).forEach(([id, chat]) => {
      mockChatHistory.set(id, chat);
    });
  }
};

// 앱 시작 시 저장된 데이터 로드
loadSavedHistory();

// 채팅 관련 API
export const chatAPI = {
  // 채팅 히스토리 가져오기
  getChatHistory: async () => {
    const history = Array.from(mockChatHistory.values());
    return { data: history };
  },

  // 새 채팅 생성
  createChat: async (modelId) => {
    const userInfo = JSON.parse(localStorage.getItem('userInfo'));
    if (!userInfo) throw new Error('사용자 정보가 없습니다.');
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    const chatId = `${userInfo.email}_${timestamp}_${random}`;
    const newChat = {
      id: chatId,
      title: '새 대화',
      created_at_ms: timestamp,
      created_at: new Date().toISOString(),
      messages: [],
      userEmail: userInfo.email,
      model: modelId || localStorage.getItem('currentModelId') || 'GPT-4o-mini'
    };
    mockChatHistory.set(chatId, newChat);
    localStorage.setItem('chatHistory', JSON.stringify(Array.from(mockChatHistory.entries())));
    return { data: newChat };
  },

  // 특정 채팅의 메시지 목록 가져오기
  getMessages: async (chatId) => {
    const chat = mockChatHistory.get(chatId);
    if (!chat) {
      throw new Error('채팅을 찾을 수 없습니다.');
    }
    return { 
      data: chat.messages,
      model: chat.model
    };
  },

  // 메시지 저장
  saveMessage: async (chatId, message, modelId) => {
    const chat = mockChatHistory.get(chatId);
    if (chat) {
      // 첫 메시지인 경우 모델 정보 저장
      if (chat.messages.length === 0 && message.role === 'user') {
        chat.model = modelId || localStorage.getItem('currentModelId') || 'GPT-4o-mini';
        mockChatHistory.set(chatId, chat);
        localStorage.setItem('chatHistory', JSON.stringify(Array.from(mockChatHistory.entries())));
      }
      chat.messages.push(message);
      localStorage.setItem('chatHistory', JSON.stringify(Array.from(mockChatHistory.entries())));
    }
    return { data: { success: true } };
  },

  // 채팅 삭제
  deleteChats: async (chatIds) => {
    chatIds.forEach(id => {
      mockChatHistory.delete(id);
    });
    localStorage.setItem('chatHistory', JSON.stringify(Array.from(mockChatHistory.entries())));
    return { data: { success: true } };
  },

  // 토큰 사용량 조회
  getTokenUsage: async () => {
    const userInfo = JSON.parse(localStorage.getItem('userInfo'));
    const token = localStorage.getItem('token');
    if (!userInfo || !userInfo.email || !token) {
      return { data: { percentage: 0, used: 0, total: 0 } };
    }
    try {
      const res = await fetch(`${process.env.VUE_APP_SECURE_AI_CLIENT_SDK_URL}/api/v1/users/tokens?userId=${encodeURIComponent(userInfo.email)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      if (data && data.success && data.data) {
        const used = data.data.usedToken || 0;
        const total = data.data.monthlyLimit || 1;
        const percentage = Math.min(100, Math.floor((used / total) * 100));
        return { data: { percentage, used, total } };
      } else {
        return { data: { percentage: 0, used: 0, total: 0 } };
      }
    } catch (e) {
      return { data: { percentage: 0, used: 0, total: 0 } };
    }
  }
};

// 모델 관련 API
export const modelAPI = {
  // 현재 모델 정보 가져오기
  getCurrentModel: async () => {
    const modelId = localStorage.getItem('currentModelId') || 'GPT-4o-mini';
    return {
      data: {
        id: modelId,
        name: modelId === 'GPT-4o-mini' ? 'GPT-4o-mini' :
              modelId === 'GPT-4o' ? 'GPT-4o' :
              modelId === 'GPT-4o-large' ? 'GPT-4o Large' : 'GPT-4o-mini'
      }
    };
  },

  // 사용 가능한 모델 목록 가져오기
  getModels: async () => {
    return {
      data: [
        { id: 'GPT-4o-mini', name: 'GPT-4o-mini' },
        { id: 'GPT-4o', name: 'GPT-4o' },
        { id: 'GPT-4o-large', name: 'GPT-4o Large' }
      ]
    };
  },

  // 모델 변경
  setModel: async (modelId) => {
    localStorage.setItem('currentModelId', modelId);
    return { data: { success: true } };
  }
};

// 인증 관련 API
export const authAPI = {
  // 로그인
  login: (credentials) => api.post('/auth/login', credentials),
  
  // 로그아웃
  logout: async () => {
    // JWT 캐시 정리
    try {
      clearJWTCache();
      console.log('[Logout] JWT 캐시 정리 완료');
    } catch (e) {
      console.error('[Logout] JWT 캐시 정리 중 오류:', e);
    }

    // 먼저 로컬 스토리지 클리어
    localStorage.removeItem('token')
    localStorage.removeItem('userInfo')
    localStorage.removeItem('lastSessionId')
    localStorage.removeItem('chat_temp_cache')

    // 백그라운드에서 서버에 로그아웃 요청
    // try {
    //   await api.post('/auth/logout')
    // } catch (error) {
    //   console.error('로그아웃 요청 중 오류:', error)
    //   // 이미 로컬 데이터는 삭제되었으므로 에러는 무시
    // }
  },
  
  // 사용자 정보 조회
  getCurrentUser: () => api.get('/auth/user')
};

// 보안 기능 관련 API
export const securityAPI = {
  // 암호화 처리
  processEncryption: async (text) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.VUE_APP_SECURE_AI_CLIENT_SDK_URL}/sgt/v1/secure/api/chat`,
        { message: text },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        }
      );
      return { data: response.data };
    } catch (error) {
      console.error('암호화 처리 중 오류:', error);
      throw new Error('암호화 처리 중 오류가 발생했습니다.');
    }
  },

  // 필터링 처리
  processFiltering: async (text) => {
    try {
      const response = await axios.post(
        `${process.env.VUE_APP_SECURE_AI_CLIENT_SDK_URL}/api/v1/detect/toxic`,
        { message: text },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${INITIAL_JWT}`
          }
        }
      );
      return { data: response.data };
    } catch (error) {
      if (error.response) {
        console.error('필터링 API 응답 에러:', error.response);
      } else if (error.request) {
        console.error('필터링 API 네트워크 에러:', error.request);
      } else {
        console.error('필터링 API 기타 에러:', error.message);
      }
      if (error.message && error.message.includes('CORS')) {
        console.error('CORS 정책에 의해 차단되었습니다. 서버에서 Access-Control-Allow-Origin 헤더를 확인하세요.');
      }
      throw new Error('필터링 처리 중 오류가 발생했습니다.');
    }
  },

  // 마스킹 처리
  processMasking: async (text) => {
    try {
      const response = await axios.post(
        `${process.env.VUE_APP_SECURE_AI_CLIENT_SDK_URL}/api/v1/detect/pii`,
        { message: text },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${INITIAL_JWT}`
          }
        }
      );
      return { data: response.data };
    } catch (error) {
      if (error.response) {
        console.error('마스킹 API 응답 에러:', error.response);
      } else if (error.request) {
        console.error('마스킹 API 네트워크 에러:', error.request);
      } else {
        console.error('마스킹 API 기타 에러:', error.message);
      }
      if (error.message && error.message.includes('CORS')) {
        console.error('CORS 정책에 의해 차단되었습니다. 서버에서 Access-Control-Allow-Origin 헤더를 확인하세요.');
      }
      throw new Error('마스킹 처리 중 오류가 발생했습니다.');
    }
  }
};

// 역할 관리 관련 API
export const roleAPI = {
  // 역할 목록 조회
  getRoles: async () => {
    return {
      data: [
        { id: '1', name: '개발팀', type: '팀원' },
        { id: '2', name: '인사팀', type: '팀장' },
        { id: '3', name: '마케팅팀', type: '팀장' },
        { id: '4', name: '영업팀', type: '팀원' }
      ]
    };
  },

  // 역할별 권한 조회
  getRolePermissions: async (roleId) => {
    const mockPermissions = {
      '1': ['READ', 'WRITE'],
      '2': ['READ', 'WRITE', 'APPROVE'],
      '3': ['READ', 'WRITE', 'PUBLISH'],
      '4': ['READ', 'WRITE', 'CONTACT']
    };

    return {
      data: {
        permissions: mockPermissions[roleId] || [],
        details: {
          roleId,
          timestamp: new Date().toISOString()
        }
      }
    };
  },

  // 역할 업데이트
  updateRole: async (roleId, data) => {
    return {
      data: {
        success: true,
        updatedRole: {
          id: roleId,
          ...data,
          updatedAt: new Date().toISOString()
        }
      }
    };
  }
};

// 보안 API 추가
export const secureChatAPI = {
  // 채팅 생성
  createChat: async () => {
    try {
      const SDK_URL = process.env.VUE_APP_SECURE_AI_CLIENT_SDK_URL;
      const response = await fetch(`${SDK_URL}/sgt/v1/secure/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'createChat', stream: false })
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      return data;
    } catch (error) {
      console.error('보안 채팅 생성 실패:', error);
      throw error;
    }
  },

  // 메시지 전송 (SDK v1.2.1: SecureMode가 활성화된 상태에서 표준 fetch 사용)
  sendMessage: async (chatId, message, options = {}) => {
    try {
      const LLM_URL = process.env.VUE_APP_LLM_URL;

      console.log('[sendMessage] options', options);

      const response = await fetch(`${LLM_URL}/api/chat`, {
        method: 'POST',
        sessionId: chatId,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...message }),
        signal: options.signal
      });

      if (options.returnStream) {
        console.log('[sendMessage] 스트림 직접 반환');
        return response.body;
      }

      const data = await response.json();
      console.log('[sendMessage] 비스트리밍 응답:', { httpStatus: response.status, ok: response.ok, data });
      // 에러 응답은 HTTP 상태코드를 포함하여 반환 (processApiRequest에서 분기 처리)
      if (!response.ok || data.error) {
        return { ...data, status: response.status };
      }
      return data;
    } catch (error) {
      console.error('보안 메시지 전송 실패:', error);
      throw error;
    }
  },

  // 채팅 히스토리 조회
  getChatHistory: async () => {
    try {
      const SDK_URL = process.env.VUE_APP_SECURE_AI_CLIENT_SDK_URL;
      const response = await fetch(`${SDK_URL}/sgt/v1/secure/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getHistory', stream: false })
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      return data;
    } catch (error) {
      console.error('보안 채팅 히스토리 조회 실패:', error);
      throw error;
    }
  },

  // 모델 목록 조회 (하드코딩)
  getModels: async () => {
    return getDefaultModels();
  }
};


// 필터링 API
export const filterAPI = {
  moderateText: async (text) => {
    try {
      const response = await axios.post(
        `${process.env.VUE_APP_SECURE_AI_CLIENT_SDK_URL}/api/v1/detect/toxic`,
        { message: text },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${INITIAL_JWT}`
          }
        }
      );
      const predictions = response.data?.data?.predictions || [];
      const isInappropriate = predictions.length > 0;
      const predict = predictions[0]?.predict || null;
      const confidence = predictions[0]?.confidence ?? null;
      return {
        isInappropriate, // true면 부적절, false면 정상
        predict,         // 부적절 유형(있으면)
        confidence       // 신뢰도(있으면)
      };
    } catch (error) {
      if (error.response) {
        console.error('필터링 API 응답 에러:', error.response);
      } else if (error.request) {
        console.error('필터링 API 네트워크 에러:', error.request);
      } else {
        console.error('필터링 API 기타 에러:', error.message);
      }
      if (error.message && error.message.includes('CORS')) {
        console.error('CORS 정책에 의해 차단되었습니다. 서버에서 Access-Control-Allow-Origin 헤더를 확인하세요.');
      }
      throw error;
    }
  }
};

// 마스킹 API
export const maskingAPI = {
  maskText: async (text) => {
    try {
      const response = await axios.post(
        `${process.env.VUE_APP_SECURE_AI_CLIENT_SDK_URL}/api/v1/detect/pii`,
        { message: text },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${INITIAL_JWT}`
          }
        }
      );
      const maskedText = response.data?.data?.maskedText || text;
      const detectedPii = response.data?.data?.detectedPii || [];
      return { maskedText, detectedPii };
    } catch (error) {
      if (error.response) {
        console.error('마스킹 API 응답 에러:', error.response);
      } else if (error.request) {
        console.error('마스킹 API 네트워크 에러:', error.request);
      } else {
        console.error('마스킹 API 기타 에러:', error.message);
      }
      if (error.message && error.message.includes('CORS')) {
        console.error('CORS 정책에 의해 차단되었습니다. 서버에서 Access-Control-Allow-Origin 헤더를 확인하세요.');
      }
      throw error;
    }
  }
};

// Jailbreak / Prompt Injection 탐지 API
export const jailbreakAPI = {
  detectJailbreak: async (text) => {
    try {
      const response = await axios.post(
        `${process.env.VUE_APP_SECURE_AI_CLIENT_SDK_URL}/api/v1/detect/jailbreak`,
        { message: text },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${INITIAL_JWT}`
          }
        }
      );

      // 실제 API 응답 구조에 맞게 수정
      const data = response.data?.data || {};
      const isSafe = data.isSafe !== undefined ? data.isSafe : true;
      const riskScore = data.riskScore || 0;
      const riskType = data.riskType || '';
      const detectionTimeMs = data.detectionTimeMs || 0;

      return {
        isJailbreakAttempt: !isSafe, // isSafe가 false면 Jailbreak / Prompt Injection 탐지됨
        riskScore,
        riskType,
        detectionTimeMs
      };
    } catch (error) {
      if (error.response) {
        console.error('Jailbreak / Prompt Injection 탐지 API 응답 에러:', error.response);
      } else if (error.request) {
        console.error('Jailbreak / Prompt Injection 탐지 API 네트워크 에러:', error.request);
      } else {
        console.error('Jailbreak / Prompt Injection 탐지 API 기타 에러:', error.message);
      }
      if (error.message && error.message.includes('CORS')) {
        console.error('CORS 정책에 의해 차단되었습니다. 서버에서 Access-Control-Allow-Origin 헤더를 확인하세요.');
      }
      throw error;
    }
  }
};

// Banword 탐지 API
export const banwordAPI = {
  detectBanword: async (text) => {
    try {
      const response = await axios.post(
        `${process.env.VUE_APP_SECURE_AI_CLIENT_SDK_URL}/api/v1/detect/banword`,
        { message: text },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${INITIAL_JWT}`
          }
        }
      );

      // 실제 API 응답 구조에 맞게 수정
      const data = response.data?.data || {};
      const isSafe = data.isSafe !== undefined ? data.isSafe : true;
      const riskScore = data.riskScore || 0;
      const detectedWords = data.detectedWords || [];

      return {
        isBanwordDetected: !isSafe, // isSafe가 false면 금지어 탐지됨
        riskScore,
        detectedWords
      };
    } catch (error) {
      if (error.response) {
        console.error('금지어 탐지 API 응답 에러:', error.response);
      } else if (error.request) {
        console.error('금지어 탐지 API 네트워크 에러:', error.request);
      } else {
        console.error('금지어 탐지 API 기타 에러:', error.message);
      }
      if (error.message && error.message.includes('CORS')) {
        console.error('CORS 정책에 의해 차단되었습니다. 서버에서 Access-Control-Allow-Origin 헤더를 확인하세요.');
      }
      throw error;
    }
  }
};

// 메시지 전처리: 필터링 → 마스킹 순서로 적용
export async function preprocessMessage(content) {
  // 1. 필터링
  const filterResult = await filterAPI.moderateText(content);
  if (filterResult.isInappropriate) {
    return { blocked: true, reason: filterResult.predict };
  }
  // 2. 마스킹
  const { maskedText, detectedPii } = await maskingAPI.maskText(content);
  return { blocked: false, processed: maskedText, detectedPii };
}

// 파일 다운로드 및 저장 (Content-Disposition에서 파일명 추출, 한글 지원)
export async function downloadAndSaveFile(fileId, fallbackFileName) {
  // 로그인된 사용자의 JWT 토큰 사용
  const JWT_TOKEN = localStorage.getItem('token');
  if (!JWT_TOKEN) {
    throw new Error('인증 토큰이 없습니다. 다시 로그인해주세요.');
  }
  const res = await fetch(`/files/${fileId}`, {
    headers: { 'Authorization': `Bearer ${JWT_TOKEN}` }
  });
  if (!res.ok) {
    if (res.status === 401) {
      throw new Error('인증 실패: JWT 토큰이 유효하지 않습니다');
    }
    try {
      const err = await res.json();
      throw new Error(err.msg || '다운로드 실패');
    } catch {
      throw new Error('다운로드 실패');
    }
  }
  const blob = await res.blob();
  // Content-Disposition에서 파일명 추출
  let fileName = fallbackFileName;
  const disposition = res.headers.get('Content-Disposition') || res.headers.get('content-disposition');
  if (disposition) {
    const match = disposition.match(/filename="?([^";]+)"?/);
    if (match && match[1]) {
      fileName = decodeURIComponent(escape(match[1]));
    }
  }
  // 다운로드 처리
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

/**
 * 파일 업로드 (PDF만 허용)
 * @param {File} file
 * @returns {Promise<{id, name, size, mime_type}>}
 */
export async function uploadFile(file) {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${process.env.VUE_APP_FILE_SERVER_URL}/files/upload`, {
    method: 'POST',
    body: formData
  });
  const data = await res.json();
  if (data.code === '0000') {
    return {
      id: data.data.id,
      name: data.data.name,
      size: data.data.size,
      mime_type: data.data.mime_type
    };
  } else {
    throw new Error(data.msg || '파일 업로드 실패');
  }
}

/**
 * 파일 다운로드 (Blob)
 * @param {string} fileId
 * @returns {Promise<Blob>}
 */
export async function downloadFileBlob(fileId) {
  // 로그인된 사용자의 JWT 토큰 사용
  const JWT_TOKEN = localStorage.getItem('token');
  if (!JWT_TOKEN) {
    throw new Error('인증 토큰이 없습니다. 다시 로그인해주세요.');
  }
  const res = await fetch(`${process.env.VUE_APP_FILE_SERVER_URL}/files/${fileId}`, {
    headers: { 'Authorization': `Bearer ${JWT_TOKEN}` }
  });
  if (!res.ok) throw new Error('파일 다운로드 실패');
  return await res.blob();
}

export default api;

/**
 * 채팅 히스토리 조회 (기간 필터 지원)
 * @param {Object} params
 *  ├ userId          {string}  - 필수, 사용자 ID(이메일)
 *  ├ startTime       {string}  - ISO8601 (UTC) 시작 시각, 예) 2025-05-19T00:00:00Z
 *  ├ endTime         {string}  - ISO8601 (UTC) 종료 시각, 예) 2025-06-19T00:00:00Z
 *  ├ page            {number}  - 선택, 서버가 페이지네이션을 강제할 때 사용
 *  ├ limit           {number}  - 선택, 페이지당 항목 수
 *  ├ sortBy          {string}  - 기본 'timestamp'
 *  └ sortOrder       {string}  - 기본 'desc'
 */
export async function fetchChatHistory({ userId, startTime, endTime, page, limit, sortBy = 'timestamp', sortOrder = 'desc' }) {
  const params = { userId, sortBy, sortOrder };
  if (startTime) params.startTime = startTime;
  if (endTime) params.endTime = endTime;
  if (page !== undefined && page !== null) params.page = page;
  if (limit !== undefined && limit !== null) params.limit = limit;
  return axios.get(`${process.env.VUE_APP_LOG_SERVER_URL}/api/logs`, { params });
}

/**
 * 채팅 기록 다중 삭제 (실제 API)
 * @param {string[]|number[]} requestIds - 삭제할 requestId 배열
 * @returns {Promise<AxiosResponse>}
 */
export async function deleteChatLogs(requestIds) {
  const ids = Array.isArray(requestIds) ? requestIds : [requestIds];
  const requestIdsParam = ids.join(','); // 배열을 쉼표로 구분된 문자열로 변환
  
  console.log('[deleteChatLogs] 원본 배열:', ids);
  console.log('[deleteChatLogs] 변환된 문자열:', requestIdsParam);
  
  // 서버에서 query parameter로 쉼표 구분 문자열을 요구함
  return axios.delete(`${process.env.VUE_APP_LOG_SERVER_URL}/api/logs/delete`, {
    params: { requestIds: requestIdsParam }
  });
}

// JWT 동적 관리 클래스
class RoleJWTManager {
  constructor() {
    this.cache = new Map(); // key: "team-position-userId", value: {jwt, expiry}
    this.baseJWT = null;    // 로그인 시 발급받은 기본 JWT
  }

  setBaseJWT(jwt) {
    this.baseJWT = jwt;
  }

  isExpired(cacheKey) {
    const cached = this.cache.get(cacheKey);
    return !cached || cached.expiry < Date.now();
  }

  // 역할별 JWT 발급 및 캐시
  async getRoleJWT(team, position, userId) {
    const cacheKey = `${team}-${position}-${userId}`;

    // 캐시 확인
    if (this.cache.has(cacheKey) && !this.isExpired(cacheKey)) {
      console.log(`[RoleJWTManager] 캐시에서 JWT 반환: ${cacheKey}`);
      return this.cache.get(cacheKey).jwt;
    }

    // 새로 발급
    try {
      const jwt = await this.fetchRoleJWT(team, position);
      this.cache.set(cacheKey, {
        jwt,
        expiry: Date.now() + (24 * 60 * 60 * 1000), // 24시간
        userId
      });
      console.log(`[RoleJWTManager] 새 JWT 발급 완료: ${cacheKey}`);
      return jwt;
    } catch (error) {
      console.error(`[RoleJWTManager] JWT 발급 실패: ${cacheKey}`, error);
      throw error; // 동적 발급 실패 시 오류 전파
    }
  }

  // 서버에서 역할별 JWT 발급 (user-credentials API 사용)
  async fetchRoleJWT(team, position) {
    const { INITIAL_JWT, API_ENDPOINTS, ROLES_USER_MAPPING } = await import('@/utils/constants');

    // ROLES_USER_MAPPING에서 해당 팀/직책의 userId 찾기
    const roleMapping = ROLES_USER_MAPPING.find(r =>
      r.team === team && r.position === position
    );

    if (!roleMapping) {
      throw new Error(`역할 매핑을 찾을 수 없습니다: ${team}-${position}`);
    }

    const response = await fetch(`${process.env.VUE_APP_SECURE_AI_CLIENT_SDK_URL}${API_ENDPOINTS.USER_CREDENTIALS}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${INITIAL_JWT}`
      },
      body: JSON.stringify({ userId: roleMapping.userId })
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    if (!data.success || !data.data?.jwt) {
      throw new Error('역할별 JWT 발급 실패: ' + (data.message || '알 수 없는 오류'));
    }

    return data.data.jwt;
  }


  clearCache() {
    this.cache.clear();
    this.baseJWT = null;
    console.log('[RoleJWTManager] 캐시 및 기본 JWT 초기화 완료');
  }
}

// 동적 JWT 관리 인스턴스
export const roleJWTManager = new RoleJWTManager();

// 역할별 JWT 조회 함수
export async function getRoleJWT(team, position, userId) {
  return await roleJWTManager.getRoleJWT(team, position, userId);
}

// JWT 관리자 초기화 (로그인 시 호출)
export function initializeJWTManager(baseJWT) {
  roleJWTManager.setBaseJWT(baseJWT);
}

// JWT 캐시 정리 (로그아웃 시 호출)
export function clearJWTCache() {
  roleJWTManager.clearCache();
}

// 사용 가능한 역할 목록
export const availableRoles = [
  { team: '재무팀', position: '팀장' },
  { team: '재무팀', position: '팀원' },
  { team: '개발팀', position: '팀장' },
  { team: '개발팀', position: '팀원' },
  { team: '인사팀', position: '팀원' },
  { team: '감사팀', position: '팀원' }
];


// 서버 응답 정규화 함수
export function normalizeChatResponse(response) {
  console.log('[normalizeChatResponse input]', JSON.stringify(response, null, 2));
  // 1. { content: ... }
  if (response && typeof response.content === 'string') {
    const refs = response.reference_docs || response.refResources || [];
    return {
      role: 'assistant',
      content: response.content,
      metadata: response.metadata || null,
      reference_docs: refs,
      created_at: response.created_at || null,
      isError: response.isError || false
    };
  }
  // 2. { message: { ... }, ... }
  if (response && response.message && typeof response.message.content === 'string') {
    const refs = response.reference_docs || response.refResources || [];
    return {
      role: response.message.role || 'assistant',
      content: response.message.content,
      metadata: response.message.metadata || null,
      reference_docs: refs,
      created_at: response.created_at || response.message.created_at || null,
      isError: response.message.isError || response.isError || false
    };
  }
  throw new Error('서버 응답 형식이 올바르지 않습니다.');
}

/**
 * 사용자별 세션 요약 목록 조회
 * @param {Object} params
 *  ├ userId     {string}   - 필수, 사용자 이메일
 *  ├ startTime  {string}   - ISO8601 (UTC) 시작 시각
 *  └ endTime    {string}   - ISO8601 (UTC) 종료 시각
 * @returns {Promise<AxiosResponse<Array<{sessionId,timestamp,content}>>>}
 */
export async function fetchSessionSummaries({ userId, startTime, endTime }) {
  const params = { userId };
  if (startTime) params.startTime = startTime;
  if (endTime) params.endTime = endTime;

  const url = `${process.env.VUE_APP_LOG_SERVER_URL}/api/sessions/summary`;
  const res = await axios.get(url, { params });
  // API 응답: { total:number, sessions:[ {sessionId,timestamp,content}, ... ] }
  return res.data.sessions || [];
}

/**
 * 주어진 sessionId 의 전체 메시지 조회 (최신 API 가 없다면 기존 fetchChatHistory fallback)
 * @param {string} sessionId
 * @returns {Promise<Array>} messages 배열
 */
export async function fetchSessionMessages(sessionId) {
  const url = `${process.env.VUE_APP_LOG_SERVER_URL}/api/logs`;
  const params = { sessionId, sortBy: 'timestamp', sortOrder: 'asc' };
  const res = await axios.get(url, { params });
  return res.data.items || res.data || [];
} 